/*
 * Copyright (c) 2020.
 * This file is part of the Compo B2B application.
 * (c) Compo soft - Vladimir Ganturin <gun2rin@gmail.com>
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import {HttpParams} from '@angular/common/http';
import {ChangeDetectionStrategy, Component, inject, OnInit, TemplateRef, ViewChild} from '@angular/core';
import {ParamMap} from '@angular/router';
import {ApiResponse, ExcelService, FileService, ListTable, PageApiResponse, StoreService} from 'compob2b-libs';
import {BaseComponent, Columns} from 'compob2b-table';
import {delay, Observable, of} from 'rxjs';
import {catchError, debounceTime, take, takeUntil, tap} from 'rxjs/operators';
import {translations} from '../../../../environments/translations.ru';
import {CrudConfig} from '../../interfaces/crud.config';
import {FormConfigService} from '../../services/form-config.service';
import {TuiDayRange} from '@taiga-ui/cdk'

@Component({
  selector: 'app-list',
  templateUrl: './list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class ListComponent<T> extends ListTable implements OnInit {

  @ViewChild('previewVideoTpl', {static: true}) previewVideoTpl: TemplateRef<any>;
  @ViewChild('actionTpl', {static: true}) actionTpl: TemplateRef<any>;
  @ViewChild('headerTpl', {static: true}) headerTpl: TemplateRef<any>;
  @ViewChild('relationTpl', {static: true}) relationTpl: TemplateRef<any>;
  @ViewChild('table', {static: true}) table: BaseComponent;

  data: Observable<T[]>;
  multipleElementTemplate: { [id: string]: any; } = {};
  objectSearchParams = new Map<string, string>();
  storeService = inject(StoreService);
  excelService = inject(ExcelService);
  _configSet: boolean = false;

  constructor(protected readonly formsConfig: FormConfigService,
              protected readonly fileService: FileService
  ) {
    super();
  }

  ngOnInit(): void {
    if (!this._configSet) {
      this.setConfig(this.config);
    }

    this.configure();

    this.route.queryParamMap.pipe(
      tap((params) => {
        this.loading = true;
        this.paramsProcess(params);
      }),
      debounceTime(200),
      tap((params) => {
        this.paramsProcess(params);

        this.filterData();
      }),
      catchError(err => this.handleError(err)),
      takeUntil(this.unsubscribe)
    ).subscribe();
  }

  setConfig(crudConfig: CrudConfig) {
    this._configSet = true;

    this.config = crudConfig;
    if (this.config.title) {
      this.storeService.setTitle(this.config.title);
    }

    this.uri = crudConfig.uri;
    this.columns = crudConfig.columns
      .map(col => {
        if (!col.cssClass) {
          col.cssClass = {includeHeader: true, name: 'col_' + col.key};
        }
        col.width = '';
        return col;
      });

    if (this.config.uriView !== undefined) {
      this.uriView = this.config.uriView;
    } else {
      this.uriView = this.uri;
    }
    if (this.config.uriEnable === undefined) {
      this.config.uriEnable = this.uri;
    }
    if (crudConfig.uriDelete === undefined) {
      this.config.uriDelete = this.uri;
      if (this.config.softDelete) {
        this.config.uriDelete += '/soft-delete';
      }
    }

    this.pagination.direction = crudConfig.defaultSortDirection;
    this.pagination.order = crudConfig.defaultSortColumn;
    this.pagination = {...this.pagination};
  }

  toggleDeleted() {
    this.showDeleted = !this.showDeleted;
    this.filterData();
  }

  /**
   * Delete button display condition
   * overrides in inherited components if necessary
   * @param row entity
   */
  deleteCondition(row: T) {
    return !!this.config.uriDelete || this.config.uriDelete === undefined;
  }

  /**
   * Edit button display condition
   * overrides in inherited components if necessary
   * @param row entity
   */
  editCondition(row: T) {
    return !!this.config.uriView || this.config.uriView === undefined;
  }

  toggleSearch(): void {
    this.configuration.searchEnabled = !this.configuration.searchEnabled;
    this.reset();
  }

  /**
   * Showing dialog before deleting an entity and handling delete result
   * @param id of entity
   */
  onDeleteConfirm(id: number, row: T = null) {
    this.confirm(translations.confirmDelete + ' ' + translations.record + '?', new Observable(() => {
      this.deleteItem(id).subscribe();
    }));
  }

  exportToExcel(): void {
    this.loading = true;
    this.data.pipe(take(1))
      .subscribe(data => {
        this.loading = false;
        this.xlsx(data);
      });

  }

  onEnabled(enabled: boolean, id: number) {
    const uri = this.config.uriEnable + '/enabled/' + String(id);
    this.compoApiService.put<boolean, void>(uri, enabled)
      .pipe(
        take(1),
        catchError(err => this.handleError(err)),
      )
      .subscribe(_ => {
        this.successToast(translations.updated);
        this.cdr.detectChanges();
      }, error => this.handleError(error));
  }

  /**
   * Main grid configuration and initialization
   * @see {@link https://ngx-easy-table.eu}
   */
  protected configure(): void {
    this.configuration.paginationEnabled = false;
    this.configuration.rows = this.pagination.limit;
    this.configuration.checkboxes = this.config.checkboxes;
    if (this.config.allowSearch === false) {
      this.configuration.allowSearch = false;
    }

    this.columns = this.formsConfig.setConfig(this.columns).grid().getConfig();

    this.formsConfig.setGridCellTemplate('action', this.actionTpl);
    this.formsConfig.setGridCellTemplate('header', this.headerTpl);

    if (this.config.relatedEntity) {
      this.config.relatedEntity.forEach(item => {
          this.multipleElementTemplate[item.multipleEntity] = item.multipleField;
          this.formsConfig.setGridCellTemplate(item.multipleEntity, this.relationTpl);
        }
      );
    }

    this.tableService.initFrom(this);
  }

  afterDelete(id: number) {
    this.filterData();
    this.loading = false;
  }

  protected deleteItem(id: number) {
    this.loading = true;
    this.scrollTop = false;
    this.cdr.detectChanges();

    return this.compoApiService.delete<any>(this.config.uriDelete + '/' + String(id))
      .pipe(
        catchError(err => this.handleError(err)),
        take(1),
        delay(800),
        tap(() => this.afterDelete(id))
      );
  }

  /**
   * Standard code from formly.dev example
   * @param data grid
   */
  protected async xlsx(data) {
    await this.excelService.exportToExcel(data, 'my_export');
  }

  /**
   * Main method with filtering and paginating behavior
   */
  protected filterData(): Observable<T[]> {
    this.loading = true;

    this.compoApiService.getWithParams<PageApiResponse<T[]>>(this.getListURI(), this.params)
      .pipe(
        take(1),
        catchError(err => this.handleError(err)),
        tap(response => {
          this.handleResponse(response.data);
          this.data = of(response.data.content);
          this.cdr.detectChanges();
        }),
      )
      .subscribe();

    this.cdr.detectChanges();

    return this.data;
  }

  protected getData(uri: string): Observable<ApiResponse<T[]>> {
    return this.compoApiService.get<T[]>(uri);
  }

  exportToXlsx() {
    const columns = this.columns.filter(item => item.grid === true && item.key !== 'action' && item.type !== 'multicheckbox')
      .map(item => this.sliceColumns(item));
    const request = {
      columns,
      sorting: {key: this.pagination.order, direction: this.pagination.direction},
      showDeleted: this.showDeleted
    };
    this.loading = true;

    this.fileService.downloadPostUrl(this.uri + '/export', request).pipe(
      take(1),
      catchError(err => this.handleError(err)),
      tap((response: Blob) => this.handleDownloadResponse(response, this.config.title + '.xlsx'))
    ).subscribe();
  }

  handleDownloadResponse(response: Blob, name: string) {
    if (response.size !== 0) {
      FileService.saveAs(response, name);
    }
    this.loading = false;
  }

  sliceColumns(item: Columns) {
    const keyword = this.params.has(item.key) ? this.params.get(item.key) : '';
    return {key: item.key, title: item.title, keyword};
  }

  paramsProcess(data: ParamMap) {
    this.updatePagerFromUri();

    if (!data.keys.length) {
      this.params = new HttpParams();
    }

    for (const key of this.objectSearchParams.keys()) {
      if (!data.get(key)) {
        this.params = this.params.delete(key);
      }
    }

    this.objectSearchParams.clear();
    data.keys.forEach(key => {
      if (key !== 'page' && key !== 'limit' && key !== 'direction' && key !== 'sort' && key !== 'order') {
        this.params = this.params.set(key, data.get(key));
        this.objectSearchParams.set(key, data.get(key));
      }
    });

    this.columns.forEach((item) => {
      const filterInpput = document.getElementById(`search_${item.key}`) as HTMLInputElement;

      if (filterInpput) {
        filterInpput.value = this.objectSearchParams.has(item.key) ? this.objectSearchParams.get(item.key) : '';
        if (filterInpput.value) {
          this.configuration.searchEnabled = true;
        }
      }
    });

    this.cdr.detectChanges();
  }

  reset(): void {
    this.resetSelected();

    let params = {};
    for (const key of this.objectSearchParams.keys()) {
      if (key != 'catalog_id') {
        params[key] = null;
      }
    }
    this.params = new HttpParams();
    this.objectSearchParams.clear();
    this.onNavigate(params);
  }

  dateFilter(dayRange: TuiDayRange): void {
    const start = dayRange.from.getFormattedDay('YMD', '-');
    const end = dayRange.to.getFormattedDay('YMD', '-');

    this.params = this.params.set('dateStart', start);
    this.params = this.params.set('dateEnd', end);
    this.router.navigate([], {
      queryParams: {
        dateStart: start,
        dateEnd: end,
        page: 1
      },
      queryParamsHandling: 'merge'
    });
  }

}
